import {
  AlertCircle,
  ArrowLeft,
  Bell,
  CheckCheck,
  CheckCircle,
  Inbox,
  MessageSquare,
  Search,
  ThumbsUp,
  UserPlus,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Admin_GetMessageList,
  Admin_GetNotifications,
  Admin_MarkNotificationRead,
} from '../../../constants/adminData';
import { NotificationItem } from '../../../types/admin';
import { normalizeNotificationType, notificationTypes } from '../../../constants/notificationTypes';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getSensitiveData } from '../../../utils/secureStorage';
import { decrementUnreadNotificationsCount, refreshNotifications, setUnreadNotificationsCount } from '../../../redux/slices/userProfileSlice';
import Toast from '../../../components/Toast/Toast';
import { formatDate } from '@/utils/useUtil';
import { useTheme } from '../../../context/ThemeContext';

interface NotificationsProps {
  onBack?: () => void;
  onOpenChat?: (chat: {
    receiverId: string;
    userName: string;
    photoId?: string;
    scrollToMessageId?: number;
  }) => void;
}

export default function Notifications({ onBack, onOpenChat }: NotificationsProps) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.userProfile);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'read'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const markingNotificationIdsRef = useRef(new Set<number>());

  useEffect(() => {
    const loadAuthToken = async () => {
      const token = profile.authToken || (await getSensitiveData('authToken')) || '';
      setAuthToken(token);
    };
    loadAuthToken();
  }, [profile.authToken]);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        if (!authToken) return;

        const response = await Admin_GetNotifications(authToken);

        if (Array.isArray(response)) {
          setNotifications(response);
          dispatch(setUnreadNotificationsCount(response.filter((notification) => !notification.isRead).length));
        } else if (response?.success && response.data?.notifications && Array.isArray(response.data.notifications)) {
          setNotifications(response.data.notifications);
          dispatch(setUnreadNotificationsCount(response.data.unreadCount));
        } else if (response?.success && Array.isArray(response.data)) {
          setNotifications(response.data);
          dispatch(setUnreadNotificationsCount(response.data.filter((notification) => !notification.isRead).length));
        } else {
          setNotifications([]);
        }
      } catch (err: any) {
        console.error('Error loading notifications:', err);
        setError(err.message || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [authToken, dispatch, profile.notificationRefreshVersion]);

  const markNotificationRead = async (notification: NotificationItem) => {
    if (notification.isRead || !authToken || markingNotificationIdsRef.current.has(notification.notificationId)) return;

    markingNotificationIdsRef.current.add(notification.notificationId);
    try {
      await Admin_MarkNotificationRead(String(notification.notificationId), authToken);
      setNotifications((current) => current.map((item) =>
        item.notificationId === notification.notificationId ? { ...item, isRead: true } : item
      ));
      dispatch(decrementUnreadNotificationsCount());
      dispatch(refreshNotifications());
    } catch (err) {
      console.error('Failed to mark notification read:', err);
      setToast({ message: 'Failed to mark notification as read. Please try again.', type: 'error' });
    } finally {
      markingNotificationIdsRef.current.delete(notification.notificationId);
    }
  };

  const getIconComponent = (type: string) => {
    switch (type) {
      case 'profile_image_interaction':
      case 'profile_viewed':
        return UserPlus;
      case 'new_match':
      case 'updated_matches':
      case 'message_reaction':
        return ThumbsUp;
      case 'conversation_initiated':
      case 'new_message':
      case 'new_group_message':
        return MessageSquare;
      case 'subscription_expired':
      case 'account_warning':
        return AlertCircle;
      case 'system_announcement':
        return Bell;
      case 'subscription_activated':
      default:
        return CheckCircle;
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'new_match':
      case 'updated_matches':
      case 'message_reaction':
        return 'text-[#FF6584]';
      case 'conversation_initiated':
      case 'new_message':
      case 'new_group_message':
      case 'system_announcement':
      case 'account_warning':
        return 'text-brand-primary';
      case 'subscription_activated':
        return 'text-[#4CAF50]';
      case 'subscription_expired':
        return 'text-[#FF9800]';
      default:
        return 'text-[#757575]';
    }
  };

  const formatNotificationType = (type: string) => {
    switch (type) {
      case 'profile_image_interaction':
        return 'Profile Interaction';
      case 'profile_viewed':
        return 'Profile View';
      case 'new_match':
        return 'Match';
      case 'updated_matches':
        return 'Updated Matches';
      case 'conversation_initiated':
        return 'Conversation';
      case 'new_message':
        return 'Message';
      case 'new_group_message':
        return 'Group Message';
      case 'message_reaction':
        return 'Message Reaction';
      case 'subscription_activated':
        return 'Subscription Activated';
      case 'subscription_expired':
        return 'Subscription Expired';
      case 'system_announcement':
        return 'Announcement';
      case 'account_warning':
        return 'Account Warning';
      default:
        return type.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };

  const formatRelativeTime = (value: string) => {
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return '';
    const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    if (diffSeconds < 60) return 'Just now';
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(value).toLocaleDateString();
  };

  const navigateToPersonalChat = async (referenceId: number, scrollToMessageId?: number) => {
    const response = await Admin_GetMessageList(authToken);
    const conversations = response?.data ?? [];
    const conversation = Array.isArray(conversations)
      ? conversations.find((item) => item.conversationId === referenceId)
      : undefined;

    if (conversation) {
      if (onOpenChat) {
        onOpenChat({
          receiverId: conversation.otherUserId,
          userName: conversation.otherUserName,
          photoId: conversation.photoId,
          scrollToMessageId,
        });
      } else {
        const photoParam = conversation.photoId ? `?photoId=${conversation.photoId}` : '';
        navigate(
          `/messageChat/${conversation.otherUserId}/${encodeURIComponent(conversation.otherUserName)}${photoParam}`,
          scrollToMessageId ? { state: { scrollToMessageId } } : undefined,
        );
      }
    } else {
      navigate('/OverViewMessages');
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    await markNotificationRead(notification);
    const normalizedNotificationType = normalizeNotificationType(notification.notificationType);

    if (normalizedNotificationType === notificationTypes.availabilityUpdatedByEmployee) {
      navigate('/availability-approvals', {
        state: notification.referenceId ? { openAvailabilityId: notification.referenceId } : undefined,
      });
      return;
    }

    if (normalizedNotificationType === notificationTypes.availabilityUpdatedByApprover) {
      navigate('/IT-AvailabilityType', { state: { section: 'availability' } });
      return;
    }

    const isPersonalMessage =
      notification.notificationType === 'new_message' ||
      notification.notificationType === 'conversation_initiated' ||
      notification.notificationType === 'message_reaction';

    if (isPersonalMessage) {
      if (notification.referenceId) {
        await navigateToPersonalChat(notification.referenceId, notification.childReferenceId ?? undefined);
      } else {
        navigate('/OverViewMessages');
      }
      return;
    }

    if (notification.notificationType === 'new_group_message') {
      navigate(notification.referenceId ? `/groups/${notification.referenceId}` : '/groups', {
        state: notification.childReferenceId ? { scrollToMessageId: notification.childReferenceId } : undefined,
      });
      return;
    }

    if (notification.navigationUrl) {
      navigate(notification.navigationUrl);
      return;
    }

    switch (notification.notificationType) {
      case 'profile_image_interaction':
      case 'profile_viewed':
        navigate('/my-profile');
        break;
      case 'new_match':
      case 'updated_matches':
        navigate('/MatchProfile');
        break;
      case 'subscription_activated':
      case 'subscription_expired':
        navigate('/menu');
        break;
      default:
        break;
    }
  };

  const handleMarkAsRead = async (notification: NotificationItem) => {
    await markNotificationRead(notification);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/dashboard');
    }
  };

  const unreadCount = notifications.filter((notification) => !notification.isRead).length;
  const readCount = notifications.filter((notification) => notification.isRead).length;
  const filteredNotifications = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return notifications.filter((notification) => {
      if (activeTab === 'unread' && notification.isRead) return false;
      if (activeTab === 'read' && !notification.isRead) return false;
      if (!normalizedSearch) return true;
      const title = formatNotificationType(notification.notificationType).toLowerCase();
      const message = notification.notificationMessage.toLowerCase();
      return title.includes(normalizedSearch) || message.includes(normalizedSearch);
    });
  }, [activeTab, notifications, searchTerm]);

  if (loading) {
    return (
      <div className="min-h-[360px] rounded-lg bg-white p-8 text-center shadow-sm">
        <p className="text-base text-gray-600">Loading notifications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[360px] rounded-lg bg-white p-8 text-center shadow-sm">
        <p className="text-base text-red-600">{error}</p>
      </div>
    );
  }
 
  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleBack} aria-label="Go back" className="rounded-lg p-2 transition-colors hover:bg-white">
              <ArrowLeft className="h-5 w-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-950">Notifications</h1>
              <p className="mt-1 text-sm text-gray-600">Review account updates, messages, and system activity.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">All Notifications</p>
                <p className="mt-2 text-3xl font-bold text-gray-950">{notifications.length}</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/10">
                <Bell className="h-6 w-6 text-brand-primary" />
              </span>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Unread</p>
                <p className="mt-2 text-3xl font-bold text-gray-950">{unreadCount}</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                <Inbox className="h-6 w-6 text-blue-600" />
              </span>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Read</p>
                <p className="mt-2 text-3xl font-bold text-gray-950">{readCount}</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                <CheckCheck className="h-6 w-6 text-green-600" />
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="grid w-full max-w-md grid-cols-3 rounded-lg bg-slate-100 p-1">
                {(['all', 'unread', 'read'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`rounded-md px-4 py-2 text-sm font-semibold capitalize transition ${
                      activeTab === tab ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="relative w-full lg:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search notifications"
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 p-4">
            {filteredNotifications.length === 0 ? (
              <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                  <Bell className="h-7 w-7 text-slate-400" />
                </span>
                <p className="text-base font-semibold text-gray-900">No notifications</p>
                <p className="mt-1 text-sm text-gray-500">You're all caught up.</p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const IconComponent = getIconComponent(notification.notificationType);
                const title = formatNotificationType(notification.notificationType);

                return (
                  <article
                    key={notification.notificationId}
                    className={`rounded-xl border p-4 transition hover:border-brand-primary/30 hover:bg-slate-50 ${
                      !notification.isRead ? 'border-blue-100 bg-blue-50/50' : 'border-gray-200 bg-white'
                    }`}
                    style={!notification.isRead && theme.isDark
                      ? { backgroundColor: theme.surfaceHover, borderColor: theme.border }
                      : undefined}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="flex items-start gap-3 sm:flex-1">
                        <button
                          type="button"
                          onClick={() => handleNotificationClick(notification)}
                          className="flex h-11 w-11 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-brand-primary/10 transition hover:bg-brand-primary/20"
                          aria-label={`Open ${formatNotificationType(notification.notificationType)} notification`}
                        >
                          <IconComponent className={`h-5 w-5 ${getIconColor(notification.notificationType)}`} />
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-base font-semibold text-gray-950">{title}</h2>
                            {!notification.isRead ? (
                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                Unread
                              </span>
                            ) : (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                                Read
                              </span>
                            )}
                           
                          </div>
                          <p className="mt-1 text-sm leading-6 text-gray-600">{notification.notificationMessage}</p>
                          <p className="mt-2 text-xs text-gray-500">{formatDate(notification.createdAt.toLocaleString())}</p>
                        </div>
                      </div>

                      <div className="flex flex-shrink-0 items-center gap-2 sm:justify-end">
                        {!notification.isRead && (
                          <button
                            type="button"
                            onClick={() => handleMarkAsRead(notification)}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-white"
                          >
                            <CheckCheck className="h-4 w-4" />
                            Mark as Read
                          </button>
                        )}
                        {(notification.navigationUrl || notification.referenceId) && (
                          <button
                            type="button"
                            onClick={() => handleNotificationClick(notification)}
                            className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-hover"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </div>
                    {!notification.isRead && <span className="mt-3 block h-2 w-2 rounded-full bg-blue-600" />}
                  </article>
                );
              })
            )}
          </div>
        </div>
      </div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
